/**
 * Some of our URLs are credentials: whoever holds one can act as its owner.
 * Error reports carry page URLs (BugSnag's context, request URL and
 * breadcrumbs), so before a report leaves the browser each secret is replaced
 * with its route placeholder: `/meet/abc-defg-hij` becomes
 * `/meet/[meetingCode]`. The rest of the URL stays, so a report still says
 * which page and which request.
 *
 * The secret-bearing routes, from an audit of next/pages:
 *
 * - `/meet/<meetingCode>` and `?meetingCode=`: the join link; anyone with the
 *   code reaches that call's lobby.
 * - `/subscribe/<token>` and `/a/<token>`: sign-in links.
 * - `?token=` and `?paymentToken=`: sign-in links on the survey, consent,
 *   coach-matches, booking, change-plan, verify-coverage and merge-clients
 *   pages.
 * - `/code/<code>`: short links, which expand to any of the above.
 * - `/booking/<ref>/<type>`: cancel and reschedule links; the ref is the
 *   call's `kickoffEventRef` or Nylas booking ref, and holding it is enough to
 *   cancel or move the call. `/booking/<username>-kickoff-call` (no type) is a
 *   coach's public booking page and stays.
 * - `?code=` on `/oauth/*`: an OAuth authorization code.
 * - `/video/<token>`: a coach's video text; holding it plays the client's
 *   video. Old links carry the file id instead, which opens the same way
 *   until they close, so every `/video/` segment is templated. The
 *   address-bar guard leaves it in place (a reload must still play), so
 *   /video loads no third-party tags at all (utilities/untracked-pages.ts).
 * - `/client-survey/<token>`: the Project Predictor text's link; holding it
 *   answers that client's question. Kept in the address bar and untracked,
 *   like `/video/`.
 *
 * Other dynamic segments are ids or public slugs and stay. A new route or
 * parameter that carries a credential belongs here, and in any address-bar
 * guard that strips sign-in tokens before trackers load, so the two lists
 * stay in step.
 */

const SECRET_PARAMS = new Set(['token', 'paymentToken', 'meetingCode'])

// Next's client-side data fetches mirror the page path:
// /_next/data/<buildId>/meet/<code>.json?meetingCode=<code>.
const NEXT_DATA = String.raw`(/_next/data/[^/?#]+)?`

const SECRET_PATHS: [RegExp, string][] = [
  [
    new RegExp(`^${NEXT_DATA}/meet/[^/?#]+?(\\.json)?/?$`),
    '$1/meet/[meetingCode]$2',
  ],
  [
    new RegExp(
      `^${NEXT_DATA}/subscribe/(?!update-payment-method(?:\\.json)?/?$)[^/?#]+?(\\.json)?/?$`
    ),
    '$1/subscribe/[token]$2',
  ],
  [new RegExp(`^${NEXT_DATA}/a/[^/?#]+?(\\.json)?/?$`), '$1/a/[token]$2'],
  [new RegExp(`^${NEXT_DATA}/code/[^/?#]+?(\\.json)?/?$`), '$1/code/[code]$2'],
  [
    new RegExp(`^${NEXT_DATA}/video/[^/?#]+?(\\.json)?/?$`),
    '$1/video/[token]$2',
  ],
  [
    new RegExp(`^${NEXT_DATA}/client-survey/[^/?#]+?(\\.json)?/?$`),
    '$1/client-survey/[token]$2',
  ],
  [
    new RegExp(`^${NEXT_DATA}/booking/[^/?#]+/([^?#]+)$`),
    '$1/booking/[ref]/$2',
  ],
]

/** A path (no query or hash) with its secret segment replaced. */
export const templateSecretPath = (path: string): string => {
  for (const [pattern, template] of SECRET_PATHS) {
    if (pattern.test(path)) return path.replace(pattern, template)
  }
  return path
}

const safeDecode = (value: string) => {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '))
  } catch {
    return value
  }
}

const templateQuery = (query: string, path: string): string => {
  const isOAuth = /^(\/_next\/data\/[^/]+)?\/oauth(\/|\.json|$)/.test(path)
  return query
    .split('&')
    .map(pair => {
      const separator = pair.indexOf('=')
      if (separator === -1) return pair
      const rawKey = pair.slice(0, separator)
      const key = safeDecode(rawKey)
      if (SECRET_PARAMS.has(key) || (isOAuth && key === 'code')) {
        return `${rawKey}=[${key}]`
      }
      // A URL carried in a parameter (verify-coverage's `returnTo` holds the
      // booking link, token and all) is templated the same way.
      const rawValue = pair.slice(separator + 1)
      const value = safeDecode(rawValue)
      const templated = templateSecretsInText(value)
      return templated === value
        ? pair
        : `${rawKey}=${encodeURIComponent(templated)}`
    })
    .join('&')
}

const URL_PARTS = /^([a-z][a-z0-9+.-]*:\/\/[^/?#]*)?([^?#]*)(\?[^#]*)?(#.*)?$/i

/**
 * A URL (absolute or root-relative) with secret path segments and query
 * values replaced. Anything else comes back unchanged, character for
 * character.
 */
export const templateSecretUrl = (url: string): string => {
  const parts = URL_PARTS.exec(url)
  if (!parts) return url
  const [, origin = '', path = '', search = '', hash = ''] = parts
  const templatedPath = templateSecretPath(path)
  const templatedSearch = search
    ? `?${templateQuery(search.slice(1), path)}`
    : ''
  return `${origin}${templatedPath}${templatedSearch}${hash}`
}

// An absolute URL, or a root-relative path at the start of the text or after
// whitespace, a quote, `(`, `[`, `=`, `:` or `,`. A comma also ends a match,
// so a list of paths is read one path at a time. No lookbehind: older Safari
// can't parse it, and a syntax error here would take the error reporter down
// with it.
const URLS_IN_TEXT =
  /(^|[\s"'([=:,])((?:https?:\/\/[^\s"'<>(),]+)|(?:\/[^\s"'<>(),]+))/g

/**
 * Text (an error message, a breadcrumb's "GET <url>") with every URL in it
 * templated.
 */
export const templateSecretsInText = (text: string): string => {
  if (typeof text !== 'string' || !text.includes('/')) return text
  return text.replace(
    URLS_IN_TEXT,
    (match, prefix: string, url: string) => `${prefix}${templateSecretUrl(url)}`
  )
}

const MAX_DEPTH = 6

/**
 * A copy of `value` with every string in it templated. Breadcrumb metadata
 * holds live objects (a navigation's history state), so this never mutates.
 */
export const templateSecretsDeep = (value: unknown, depth = 0): unknown => {
  if (typeof value === 'string') return templateSecretsInText(value)
  if (depth >= MAX_DEPTH || !value || typeof value !== 'object') return value
  if (Array.isArray(value)) {
    return value.map(item => templateSecretsDeep(item, depth + 1))
  }
  const prototype = Object.getPrototypeOf(value)
  if (prototype !== Object.prototype && prototype !== null) return value
  return Object.fromEntries(
    Object.entries(value).map(([key, nested]) => [
      key,
      templateSecretsDeep(nested, depth + 1),
    ])
  )
}
